import { addDays, addHours, addMinutes, addSeconds, getDayOfYear } from 'date-fns';
import { FormEvent } from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { DatepickerValue, ICheckProps, ISelectorItemProps, IToggleProps } from 'telia-front-react';
import { DisturbanceArea, DisturbanceStatus } from '@config/DisturbanceConfig';

import {
  DisturbanceCurrentStatusFilter,
  DisturbanceClassificationFilter,
  DisturbanceClientFilter,
  DisturbanceAreaFilter,
  DisturbanceRegionFilter,
  DisturbanceStatusFilter,
  Filter,
  FilterState
} from '@config/DisturbanceFilter';
import SsoStore from '@stores/SsoStore';
import RootStore from '@stores/RootStore';
import TranslateStore from '@stores/TranslateStore';

interface CheckProps extends ICheckProps {
  isSelected: boolean;
}

interface SelectProps extends ISelectorItemProps {
  isSelected: boolean;
}

export default class FilterStore {
  @observable public client: IToggleProps;
  @observable public classification: CheckProps[];
  @observable public elementInput: string = '';
  @observable public elements: string[] = [];
  @observable public area: CheckProps[];
  @observable public currentStatus: IToggleProps;
  @observable public status: CheckProps[];
  @observable public startFrom: DatepickerValue = null;
  @observable public startUntil: DatepickerValue = null;
  @observable public region: CheckProps[];
  @observable public isOpen: boolean = false;
  @observable public filter: FilterState;

  private ssoStore: SsoStore;
  private readonly translateStore: TranslateStore;

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.ssoStore = rootStore.ssoStore;
    this.translateStore = rootStore.translateStore;
    this.resetFilters(true);
  }

  @action
  public toggleFilter = (): void => {
    this.isOpen = !this.isOpen;
  };

  @computed
  public get selectedClientOnly() {
    return this.client.checked;
  }

  @computed
  public get selectedInternalOnly() {
    return this.classification.filter((t: CheckProps) => t.isSelected);
  }

  @computed
  public get selectedElements() {
    return this.elements;
  }

  @computed
  public get selectedAreas() {
    return this.area.filter((t: CheckProps) => t.isSelected);
  }

  @computed
  public get selectedStatuses() {
    return this.status.filter((t: CheckProps) => t.isSelected);
  }

  @computed
  public get selectedCurrentStatus() {
    return this.currentStatus.checked;
  }

  @computed
  public get selectedRegions() {
    return this.region.filter((r: CheckProps) => r.isSelected);
  }

  @action
  public updateClient = (checked: boolean): void => {
    this.client.checked = checked;
    this.updateFilterState();
  };

  @action
  public updateCurrentStatus = (checked: boolean): void => {
    this.currentStatus.checked = checked;
    if (checked) {
      this.setStatusCurrent(true);
    } else {
      this.resetCurrentStatus(true);
    }
  };

  @action
  public updateInternal = (values: string[]): void => {
    this.resetInternal();
    this.setSelected(this.classification, values);
    this.updateFilterState();
  };

  @action
  public updateFromDate = (selectedValue: DatepickerValue): void => {
    this.startFrom = selectedValue;
    this.updateFilterState();
  };

  @action
  public updateUntilDate = (selectedValue: DatepickerValue): void => {
    this.startUntil = selectedValue;
    this.updateFilterState();
    this.checkCurrentStatus();
  };

  @action
  public updateArea = (values: string[]): void => {
    const previousValues = this.getValues(this.selectedAreas);
    const difference: string[] = values.filter((val: string) => !previousValues.includes(val));
    this.resetArea();
    if (difference.includes(DisturbanceArea.NOT_SET)) {
      this.setSelected(this.area, [DisturbanceArea.NOT_SET]);
    } else {
      if (values.includes(DisturbanceArea.NOT_SET)) {
        values = values.filter((area: string) => area !== DisturbanceArea.NOT_SET);
      }
      this.setSelected(this.area, values);
    }
    this.updateFilterState();
  };

  @action
  public updateElementInput = (value: string): void => {
    this.elementInput = value;
  };

  @action
  public setSingleElement = (element: string): void => {
    if (element && element.length < 50) {
      const elements = [];
      this.elements = [];
      elements.push(element);
      this.elements = elements;
      this.resetFilters(true, false, true);
      this.updateFilterState();
    }
  };

  @action
  public addElement = (event: FormEvent): void => {
    event.preventDefault();
    if (this.elementInput && this.elementInput.length < 50 && this.elements.length < 100 && this.elements.includes(this.elementInput) === false) {
      const elements = [...this.elements];
      this.elements = [];
      elements.push(this.elementInput);
      this.elements = elements;
      this.elementInput = '';
      this.resetFilters(true, false, true);
      this.updateFilterState();
    }
  };

  @action
  public updateStatus = (values: string[]): void => {
    const previousValues = this.getValues(this.selectedStatuses);
    const difference: string[] = values.filter((val: string) => !previousValues.includes(val));
    this.resetStatus();
    if (difference.includes(DisturbanceStatus.DEADEND)) {
      this.setSelected(this.status, [DisturbanceStatus.DEADEND]);
    } else {
      if (values.includes(DisturbanceStatus.DEADEND)) {
        values = values.filter((area: string) => area !== DisturbanceStatus.DEADEND);
      }
      this.setSelected(this.status, values);
    }
    this.updateFilterState();
    this.checkCurrentStatus();
  };

  @action
  public setStatusCurrent = (updateFilter: boolean = false): void => {
    this.resetStatus();
    this.resetStart();
    this.setSelected(this.status, [DisturbanceStatus.OPEN]);
    const currentDate = new Date(Date.now());
    currentDate.setHours(0);
    currentDate.setMinutes(0);
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);
    this.startUntil = currentDate;
    this.checkCurrentStatus();
    if (updateFilter) {
      this.updateFilterState();
    }
  };

  @action
  public updateRegions = (values: string[]): void => {
    this.resetRegions();
    this.setSelected(this.region, values);
    this.updateFilterState();
  };

  private checkCurrentStatus = (): void => {
    const status = this.selectedStatuses.map((status) => status.id);
    const startUntil = this.startUntil ? getDayOfYear(new Date(this.startUntil.toString())) : 0;
    this.currentStatus.checked = status.length === 1 && status[0] === DisturbanceStatus.OPEN && startUntil === getDayOfYear(Date.now());
  };

  @action
  public removeClient = (): void => {
    this.client.checked = false;
    this.updateFilterState();
  };

  @action
  public removeInternal = (internal: string): void => {
    this.removeSelected(this.classification, internal);
    this.updateFilterState();
  };

  @action
  public removeElement = (id: string): void => {
    this.elements = this.elements.filter((i: string) => i !== id);
    this.updateFilterState();
  };

  @action
  public removeArea = (id: string): void => {
    this.removeSelected(this.area, id);
    this.updateFilterState();
  };

  @action
  public removeStatus = (id: string): void => {
    this.removeSelected(this.status, id);
    this.checkCurrentStatus();
    this.updateFilterState();
  };

  @action
  public resetStartFrom = (forceUpdate: boolean = false): void => {
    this.startFrom = null;
    if (forceUpdate) {
      this.updateFilterState();
    }
  };

  @action
  public resetStartUntil = (forceUpdate: boolean = false): void => {
    this.startUntil = null;
    if (forceUpdate) {
      this.updateFilterState();
    }
    this.checkCurrentStatus();
  };

  @action
  public removeRegion = (id: string): void => {
    this.removeSelected(this.region, id);
    this.updateFilterState();
  };

  @action
  public resetCurrentStatus = (updateFilter: boolean = false): void => {
    this.currentStatus = this.getCurrentStatus;
    this.resetStatus();
    this.resetStart();
    if (updateFilter) {
      this.updateFilterState();
    }
  };

  @action
  public resetFilters = (resetClient: boolean = false, resetElements: boolean = true, resetCurrent: boolean = false): void => {
    if (this.ssoStore.isDealer || resetClient) {
      this.resetClient();
    }
    this.resetInternal();
    this.resetArea();
    this.resetStatus();
    if (resetElements) {
      this.resetElements();
    }
    this.resetCurrentStatus();
    this.resetStart();
    this.resetRegions();
    if (this.ssoStore.isDealer && !resetCurrent) {
      this.setStatusCurrent();
    }
    this.updateFilterState();
  };

  private resetInternal = (): void => {
    this.classification = this.getInternal;
  };

  private resetClient = (): void => {
    this.client = this.getClient;
  };

  private resetArea = (): void => {
    this.area = this.getArea;
  };

  private resetElements = (): void => {
    this.elements = [];
  };

  private resetStatus = (): void => {
    this.status = this.getStatus;
  };

  private resetStart = (): void => {
    this.resetStartFrom();
    this.resetStartUntil();
  };

  private resetRegions = (): void => {
    this.region = this.getRegion;
  };

  private updateFilterState = (): void => {
    this.filter = {
      classification: this.selectedInternalOnly.length ? 'internal' : '',
      customerId: this.selectedClientOnly && this.ssoStore.customerId ? this.ssoStore.customerId.toString() : '',
      affectedElements: this.elements,
      area: this.getValues(this.selectedAreas),
      status: this.getValues(this.selectedStatuses),
      startFrom: this.startFrom ? this.cleanStartDate(this.startFrom.toString()) : '',
      startUntil: this.startUntil ? this.cleanStartDate(this.prepareStartUntil(this.startUntil.toString()).toString()) : '',
      region: this.getValues(this.selectedRegions)
    };
  };

  private getValues = (arr: SelectProps[] | CheckProps[]): string[] => {
    return arr.map((el) => el.value);
  };

  private prepareStartUntil = (dateString: string): Date => {
    return addHours(addMinutes(addSeconds(new Date(dateString), 59), 59), 23);
  };

  private cleanStartDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString();
  };

  @computed
  get getInternal(): CheckProps[] {
    return this.prepareCheckProps(DisturbanceClassificationFilter);
  }

  @computed
  get getClient(): IToggleProps {
    return this.prepareToggleProps(DisturbanceClientFilter);
  }

  @computed
  get getCurrentStatus(): IToggleProps {
    return this.prepareToggleProps(DisturbanceCurrentStatusFilter);
  }

  @computed
  get getArea(): CheckProps[] {
    return this.prepareCheckProps(DisturbanceAreaFilter);
  }

  @computed
  get getStatus(): CheckProps[] {
    return this.prepareCheckProps(DisturbanceStatusFilter);
  }

  @computed
  get getRegion(): CheckProps[] {
    return this.prepareCheckProps(DisturbanceRegionFilter, false);
  }

  private prepareToggleProps = (obj: Filter): IToggleProps => {
    const { translate } = this.translateStore;
    return {
      id: obj.value,
      label: obj.text.includes('.') ? translate(obj.text) : obj.text,
      defaultChecked: false,
      checked: false
    };
  };

  private prepareSelectorProps = (arr: Filter[]): SelectProps[] => {
    const { translate } = this.translateStore;
    return arr.map((obj) => ({
      id: obj.value,
      label: obj.text.includes('.') ? translate(obj.text) : obj.text,
      name: obj.value,
      value: obj.value,
      type: 'employee',
      isSelected: false
    }));
  };

  private prepareCheckProps = (arr: Filter[], employee: boolean = true): CheckProps[] => {
    const { translate } = this.translateStore;
    return arr.map((obj) => ({
      id: obj.value,
      value: obj.value,
      labelText: obj.text.includes('.') ? translate(obj.text) : obj.text,
      children: obj.text.includes('.') ? translate(obj.text) : obj.text,
      employee: employee,
      employeeBackground: false,
      isSelected: false
    }));
  };

  private removeSelected = (arr: any[], value: string): void => {
    const foundArr = arr.filter((el) => el.value === value && el.isSelected);
    foundArr.forEach((el) => {
      el.isSelected = false;
    });
  };

  private setSelected = (arr: any[], values: string[]): void => {
    values.forEach((value) => {
      const indexOfRegion = arr.findIndex((region: ICheckProps) => region.value === value);
      arr[indexOfRegion].isSelected = !arr[indexOfRegion].isSelected;
    });
  };
}
